// App.js
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { initializeSocket } from "./utils/socket";
import Login from "./components/Login";
import Register from "./components/Register";
import ForgotPassword from "./components/ForgotPassword";
import Dashboard from "./components/Dashboard";
import Profile from "./components/Profile";
import EditProfile from "./components/EditProfile";
import SearchAndSuggest from "./components/SearchAndSuggest";
import HomePage from "./components/HomePage";
import RealtimeChat from "./components/RealtimeChat";
import ChatList from "./components/ChatList";
import ChatPage from "./components/ChatPage";
import AdminDashboard from "./components/admin/AdminDashboard";
import UserManagement from "./components/admin/UserManagement";
import RoleSettings from "./components/admin/RoleSettings";
import AdminMessages from "./components/admin/AdminMessages";
import EventManagement from "./components/admin/EventManagement";
import SystemSettings from "./components/admin/SystemSettings";
import EmailConfig from "./components/admin/EmailConfig";
import GeneralSettings from "./components/admin/GeneralSettings";
import Reports from "./components/admin/Reports";
import EventDetail from "./components/EventDetail";
import MembershipPlansManagement from "./components/admin/MembershipPlansManagement";
import PaymentManagement from "./components/admin/PaymentManagement";
import UpgradeAccount from "./components/UpgradeAccount";
import PaymentGatewaySettings from "./components/admin/PaymentGatewaySettings";
import LocalPaymentGatewaySettings from "./components/admin/LocalPaymentGatewaySettings";
import EmailNotifications from "./components/EmailNotifications";
import PhotoStorageSettings from "./components/admin/PhotoStorageSettings";
import PhotoUploader from "./components/PhotoUploader";
import MatchingCriteria from "./components/MatchingCriteria";
import AdminCoinPackages from "./components/admin/AdminCoinPackages";
import AdminCoinTransactions from "./components/admin/AdminCoinTransactions";
import AdminUserCoinBalance from "./components/admin/AdminUserCoinBalance";
import CoinPurchase from "./components/CoinPurchase";
import GiftManagement from "./components/admin/GiftManagement";
import SendGift from "./components/SendGift";
import CoinManagement from "./components/CoinManagement";
import AdminCoinWithdrawals from "./components/admin/AdminCoinWithdrawals";
import AdminCoinSettings from "./components/admin/AdminCoinSettings";
import AdminWithdrawalPaymentGateways from "./components/admin/AdminWithdrawalPaymentGateways";
import AdminCoinToVipPlans from "./components/admin/AdminCoinToVipPlans";
import AdminCoinToVipRequests from "./components/admin/AdminCoinToVipRequests";
import ChatRestrictionSettings from "./components/admin/ChatRestrictionSettings"; // Thêm import
import OnlineUsersPage from "./components/OnlineUsersPage";
import AdvancedSearch from "./components/AdvancedSearch";
import SearchResults from "./components/SearchResults";
import FriendsPage from "./components/FriendsPage";
import LikePage from "./components/LikePage";
import { useAuth } from "./context/AuthContext";
import { useEffect } from "react";

// Kiểm tra component hợp lệ
const isValidComponent = (Component) => {
  if (!Component || typeof Component !== "function") {
    console.error(`Component không hợp lệ: ${Component}`);
    return false;
  }
  return true;
};

// Component để bảo vệ các route dựa trên quyền
const ProtectedRoute = ({ children, allowedPermissions }) => {
  const { user, isLoading } = useAuth();

  console.log("ProtectedRoute - isLoading:", isLoading, "user:", user);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid"></div>
      </div>
    );
  }

  if (!user) {
    console.log("Người dùng chưa đăng nhập, chuyển hướng về /login");
    return <Navigate to="/login" />;
  }

  if (user.status === "banned") {
    console.log("Người dùng bị khóa, chuyển hướng về /login");
    return <Navigate to="/login" />;
  }

  if (allowedPermissions && allowedPermissions.length > 0) {

    // Nếu user là role 'user', cho phép bỏ qua permission
    if (user.role === "user") {
        return children;
    }

    const userPermissions = user.permissions || [];
    const hasPermission = allowedPermissions.some((perm) =>
        userPermissions.includes(perm)
    );



    if (!hasPermission) {
      console.log("Người dùng không có quyền truy cập:", {
        requiredPermissions: allowedPermissions,
        userPermissions,
        role: user.role,
        path: window.location.pathname,
      });
      return <Navigate to="/dashboard" />;
    }
  }

  return children;
};

// Component con để chứa logic sử dụng useAuth
const AppContent = () => {
  const { isLoading, user, fetchWithRetry, logout } = useAuth();

  // Khởi tạo Socket.IO khi có user và token
  useEffect(() => {
    if (user && !isLoading) {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.warn("Không tìm thấy accessToken trong localStorage, bỏ qua khởi tạo socket");
        return;
      }
      try {
        const socket = initializeSocket(token);
        if (!socket.connected) {
          socket.connect();
        }
        socket.on("connect", () => {
          console.log("Socket.IO connected in App.js:", socket.id);
        });
        socket.on("connect_error", (err) => {
          console.error("Socket.IO connection error in App.js:", err.message);
          if (err.message.includes("token")) {
            console.warn("Token không hợp lệ hoặc thiếu, yêu cầu đăng nhập lại");
            // Có thể thêm logic để làm mới token hoặc đăng xuất
          }
        });
        // Cleanup khi component unmount
        return () => {
          socket.off("connect");
          socket.off("connect_error");
        };
      } catch (error) {
        console.error("Lỗi khi khởi tạo socket:", error.message);
      }
    }
  }, [user, isLoading]);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* Protected routes cho người dùng thông thường */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:membershipId"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/advanced-search"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <AdvancedSearch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search-results"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <SearchResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/edit-profile"
        element={
          <ProtectedRoute allowedPermissions={["edit_profile", "edit_users"]}>
            <EditProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <SearchAndSuggest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/homepage"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/friends"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <FriendsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/likes"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <LikePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/online-users"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <OnlineUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat-list"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <ChatList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:userId"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/realtime-chat/:friendId"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
           
            <RealtimeChat />
            
          </ProtectedRoute>
        }
      />
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <UpgradeAccount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coin-purchase"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <CoinPurchase />
          </ProtectedRoute>
        }
      />
      <Route
        path="/send-gift"
        element={
          <ProtectedRoute allowedPermissions={["view_users", "send_gifts"]}>
            <SendGift />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coin-management"
        element={
          <ProtectedRoute allowedPermissions={[]}>
            <CoinManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/event/:eventId"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <EventDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <EmailNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/photo-uploader"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <PhotoUploader fetchWithRetry={fetchWithRetry} logout={logout} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/matching-criteria"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <MatchingCriteria />
          </ProtectedRoute>
        }
      />

      {/* Protected routes cho quản trị viên */}
      <Route
        path="/admindashboard"
        element={
          <ProtectedRoute allowedPermissions={["view_users"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      >
        <Route
          path="user-management"
          element={
            <ProtectedRoute allowedPermissions={["edit_users", "delete_users"]}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="role-settings"
          element={
            <ProtectedRoute allowedPermissions={["manage_roles"]}>
              <RoleSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin-messages"
          element={
            <ProtectedRoute allowedPermissions={["view_private_messages"]}>
              <AdminMessages />
            </ProtectedRoute>
          }
        />
        <Route
          path="event-management"
          element={
            <ProtectedRoute allowedPermissions={["manage_events"]}>
              <EventManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute allowedPermissions={["configure_settings"]}>
              <SystemSettings />
            </ProtectedRoute>
          }
        >
          <Route
            path="email-config"
            element={
              <ProtectedRoute allowedPermissions={["configure_settings"]}>
                <EmailConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="general-settings"
            element={
              <ProtectedRoute allowedPermissions={["configure_settings"]}>
                <GeneralSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="photo-storage-settings"
            element={
              <ProtectedRoute allowedPermissions={["configure_settings"]}>
                <PhotoStorageSettings />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route
          path="chat-restrictions"
          element={
            <ProtectedRoute allowedPermissions={["manage_chat_restrictions"]}>
              {isValidComponent(ChatRestrictionSettings) ? <ChatRestrictionSettings /> : <div>Lỗi: Component không hợp lệ</div>}
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute allowedPermissions={["view_reports"]}>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="membership-plans"
          element={
            <ProtectedRoute allowedPermissions={["manage_membership_plans"]}>
              <MembershipPlansManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="payments"
          element={
            <ProtectedRoute allowedPermissions={["view_payments"]}>
              <PaymentManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="payment-gateways"
          element={
            <ProtectedRoute allowedPermissions={["manage_payment_gateways"]}>
              <PaymentGatewaySettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="local-payment-gateways"
          element={
            <ProtectedRoute allowedPermissions={["manage_payment_gateways"]}>
              <LocalPaymentGatewaySettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="coin-packages"
          element={
            <ProtectedRoute allowedPermissions={["manage_coins"]}>
              <AdminCoinPackages />
            </ProtectedRoute>
          }
        />
        <Route
          path="coin-transactions"
          element={
            <ProtectedRoute allowedPermissions={["manage_coins"]}>
              <AdminCoinTransactions />
            </ProtectedRoute>
          }
        />
        <Route
          path="user-coin-balance"
          element={
            <ProtectedRoute allowedPermissions={["manage_coins"]}>
              <AdminUserCoinBalance />
            </ProtectedRoute>
          }
        />
        <Route
          path="coin-to-vip-plans"
          element={
            <ProtectedRoute allowedPermissions={["manage_coins"]}>
              <AdminCoinToVipPlans />
            </ProtectedRoute>
          }
        />
        <Route
          path="coin-to-vip-requests"
          element={
            <ProtectedRoute allowedPermissions={["manage_coins"]}>
              <AdminCoinToVipRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="gift-management"
          element={
            <ProtectedRoute allowedPermissions={["manage_gifts"]}>
              <GiftManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="coin-withdrawal-requests"
          element={
            <ProtectedRoute allowedPermissions={["manage_coins"]}>
              {isValidComponent(AdminCoinWithdrawals) ? <AdminCoinWithdrawals /> : <div>Lỗi: Component không hợp lệ</div>}
            </ProtectedRoute>
          }
        />
        <Route
          path="coin-exchange-rate"
          element={
            <ProtectedRoute allowedPermissions={["manage_coins"]}>
              {isValidComponent(AdminCoinSettings) ? <AdminCoinSettings /> : <div>Lỗi: Component không hợp lệ</div>}
            </ProtectedRoute>
          }
        />
        <Route
          path="withdrawal-payment-gateways"
          element={
            <ProtectedRoute allowedPermissions={["manage_coins"]}>
              {isValidComponent(AdminWithdrawalPaymentGateways) ? <AdminWithdrawalPaymentGateways /> : <div>Lỗi: Component không hợp lệ</div>}
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Route mặc định để xử lý các URL không khớp */}
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;